import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowsClockwise, CheckCircle, Info, PencilSimple, Sparkle, Warning, XCircle, Archive, Play, Eye, Desktop, DeviceMobile, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { LONG_TIMEOUT_MS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MarketingShell from "./MarketingShell";
import { useAuth } from "@/lib/AuthContext";
import {
  STATUS_TABS, STATUS_STYLE, blockingFlags, bulkApprovalPlan, errorText, isSendable,
  preview, rate, warningFlags, copyWordCount, WORD_RANGE, PREVIEW_WIDTHS,
} from "@/lib/nurtureContent";
import { safeSrcDoc } from "@/lib/safeHtml";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const selectCls = "h-9 border border-[#E5E5E5] bg-white px-2 text-sm rounded-none";

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { label: status, cls: "bg-white text-[#525252] border-[#E5E5E5]" };
  return <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.cls}`}>{s.label}</span>;
}

function Flags({ item, full = false }) {
  const block = blockingFlags(item);
  const warn = warningFlags(item);
  if (!block.length && !warn.length) return <span className="text-[11px] text-[#047857]">Clean</span>;
  return (
    <div className="space-y-0.5">
      {block.map((f, i) => (
        <div key={`b${i}`} className="text-[11px] text-[#B91C1C] flex gap-1" title={f.message}>
          <XCircle size={12} className="mt-0.5 shrink-0" /> {full ? f.message : f.code.replace(/_/g, " ")}
        </div>
      ))}
      {warn.map((f, i) => (
        <div key={`w${i}`} className="text-[11px] text-[#92400E] flex gap-1" title={f.message}>
          <Warning size={12} className="mt-0.5 shrink-0" /> {full ? f.message : f.code.replace(/_/g, " ")}
        </div>
      ))}
    </div>
  );
}

function EmailCard({ item }) {
  return (
    <div className="border border-[#E5E5E5] bg-white p-3 space-y-2 text-sm text-[#262626]" data-testid={`nurture-result-${item.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-bold text-[#0A0A0A]">{item.subject}</p>
        <span className="text-[10px] text-[#A3A3A3] whitespace-nowrap">{item.module_label || item.theme_label} · {item.word_count ?? copyWordCount(item)} words</span>
      </div>
      <p>{item.opening}</p>
      <p>{item.feature}</p>
      <p className="font-semibold text-[#273b86]">
        {item.cta_type === "link" ? `[ ${item.cta_label} ] → ${item.cta_url || item.cta_link_key}` : item.cta_label}
      </p>
      <p className="text-xs text-[#525252]">{item.cross_sell}</p>
      <Flags item={item} full />
    </div>
  );
}

// ── Preview dialog — the exact email, rendered by the send renderer ────────
function PreviewDialog({ item, edits, onClose, onApprove }) {
  const [device, setDevice] = useState("desktop");
  const [mode, setMode] = useState("html");
  const [leadId, setLeadId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const render = useCallback(async (lead) => {
    setLoading(true); setError("");
    try {
      const r = await api.post(`/marketing/nurture-content/${item.id}/preview`,
        { ...(edits || {}), ...(lead ? { lead_id: lead } : {}) });
      setData(r.data);
    } catch (e) {
      setError(errorText(e, "The preview could not be rendered. Nothing was sent."));
    }
    setLoading(false);
  }, [item.id, edits]);

  useEffect(() => { render(null); }, [render]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-4xl max-h-[94vh] overflow-y-auto" data-testid="nurture-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-black text-xl tracking-tight" style={H}>Preview{edits ? " (unsaved changes)" : ""}</DialogTitle>
          <DialogDescription className="text-xs text-[#525252]">
            Rendered by the same template and renderer Pulse uses to send. <strong>Preview never sends email</strong>;
            tracking is off in preview.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="flex border border-[#E5E5E5]" role="tablist" aria-label="Preview width">
            {[["desktop", Desktop], ["mobile", DeviceMobile]].map(([k, Icon]) => (
              <button key={k} role="tab" aria-selected={device === k} onClick={() => setDevice(k)} data-testid={`nurture-preview-${k}`}
                className={`px-2.5 py-1.5 inline-flex items-center gap-1 ${device === k ? "bg-[#0A0A0A] text-white" : "bg-white text-[#525252]"}`}>
                <Icon size={13} /> {k === "desktop" ? "Desktop" : "Mobile"}
              </button>
            ))}
          </div>
          <div className="flex border border-[#E5E5E5]" role="tablist" aria-label="Preview format">
            {[["html", "HTML"], ["text", "Plain text"]].map(([k, lbl]) => (
              <button key={k} role="tab" aria-selected={mode === k} onClick={() => setMode(k)} data-testid={`nurture-preview-mode-${k}`}
                className={`px-2.5 py-1.5 ${mode === k ? "bg-[#0A0A0A] text-white" : "bg-white text-[#525252]"}`}>{lbl}</button>
            ))}
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <Input value={leadId} onChange={(e) => setLeadId(e.target.value)} placeholder="Lead ref or id (optional)"
              className="rounded-none h-8 w-52 text-xs" aria-label="Preview as lead" />
            <Button variant="outline" className="rounded-none h-8 text-xs" onClick={() => render(leadId.trim() || null)}>
              Personalise
            </Button>
          </div>
        </div>
        {loading && <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>}
        {!loading && error && <div className="border border-[#FECACA] bg-[#FEF2F2] p-3 text-sm text-[#B91C1C]" role="alert">{error}</div>}
        {!loading && data && (
          <div className="space-y-2">
            <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-2 text-xs">
              <div><span className="text-[#A3A3A3] uppercase tracking-wider">Subject:</span> <strong>{data.subject}</strong></div>
              <div className="text-[#737373]">{data.personalisation} · {data.word_count} words of copy</div>
            </div>
            <div className="bg-[#F5F5F5] p-3 flex justify-center">
              {mode === "html" ? (
                <iframe title="Email preview" sandbox="" referrerPolicy="no-referrer" data-testid="nurture-preview-frame"
                  srcDoc={safeSrcDoc(data.html) || ""}
                  style={{ width: PREVIEW_WIDTHS[device], maxWidth: "100%", height: "65vh", border: "1px solid #E5E5E5", background: "#fff" }} />
              ) : (
                <pre className="whitespace-pre-wrap font-mono text-xs bg-white border border-[#E5E5E5] p-3"
                  style={{ width: PREVIEW_WIDTHS[device], maxWidth: "100%" }} data-testid="nurture-preview-text">{data.text}</pre>
              )}
            </div>
            <Flags item={{ lint_flags: data.lint_flags }} full />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
          {onApprove && !edits && item.status === "draft" && (
            <Button className="rounded-none bg-[#047857] hover:bg-[#065F46] text-white" onClick={() => onApprove(item)} data-testid="nurture-preview-approve">
              <CheckCircle size={14} className="mr-1.5" /> Approve…
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Generate dialog ────────────────────────────────────────────────────────
function GenerateDialog({ options, onClose, onGenerated }) {
  const [count, setCount] = useState(10);
  const [themes, setThemes] = useState([]);
  const [tone, setTone] = useState("understated");
  const [maxWords, setMaxWords] = useState(options?.default_max_words || WORD_RANGE[1]);
  const [cta, setCta] = useState("mixed");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const toggle = (k) => setThemes((t) => (t.includes(k) ? t.filter((x) => x !== k) : [...t, k]));

  const generate = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await api.post("/marketing/nurture-content/generate",
        { count: Number(count), themes, tone, max_words: Number(maxWords), cta },
        { timeout: LONG_TIMEOUT_MS });
      setResult(r.data);
      onGenerated?.();
      toast.success(`${r.data.items.length} draft${r.data.items.length === 1 ? "" : "s"} saved for review`);
    } catch (e) {
      setError(errorText(e, "Pulse AI could not generate drafts. Nothing was saved."));
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={() => !busy && onClose()}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="nurture-generate-dialog">
        <DialogHeader>
          <DialogTitle className="font-black text-xl tracking-tight" style={H}>Generate nurture emails with AI</DialogTitle>
          <DialogDescription className="text-xs text-[#525252]">
            Pulse AI writes structured drafts — one Eyvo module per email, with an opening, a feature paragraph,
            a call to action and a short cross-sell. Pulse adds the standard Eyvo header, signature and footer.
            Every result is saved as a <strong>draft</strong>; nothing is sent until an admin approves it.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">How many</Label>
                <Input type="number" min={1} max={options?.max_generate || 25} value={count}
                  onChange={(e) => setCount(e.target.value)} className="rounded-none" data-testid="nurture-gen-count" />
              </div>
              <div>
                <Label className="text-xs">Tone</Label>
                <select className={`${selectCls} w-full`} value={tone} onChange={(e) => setTone(e.target.value)}>
                  {(options?.tones || []).map((t) => <option key={t.key} value={t.key}>{t.key}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs" title="Words of copy before the footer">Max words</Label>
                <Input type="number" min={70} max={130} value={maxWords}
                  onChange={(e) => setMaxWords(e.target.value)} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs">Call to action</Label>
                <select className={`${selectCls} w-full`} value={cta} onChange={(e) => setCta(e.target.value)}>
                  <option value="mixed">Mixed (links and replies)</option>
                  {(options?.cta_types || []).map((c) => <option key={c.key} value={c.key}>{c.key.replace(/_/g, " ")}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Modules <span className="text-[#A3A3A3]">(none selected = all modules)</span></Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {(options?.themes || []).map((t) => (
                  <button key={t.key} type="button" onClick={() => toggle(t.key)} title={t.facts}
                    className={`text-[11px] px-2 py-1 border ${themes.includes(t.key) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0A0A0A]"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            {error && (
              <div className="border border-[#FECACA] bg-[#FEF2F2] p-3 text-sm text-[#B91C1C]" role="alert">{error}</div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-white" onClick={generate} disabled={busy} data-testid="nurture-gen-submit">
                <Sparkle size={14} className="mr-1.5" />
                {busy ? "Writing drafts… (this can take up to a minute)" : "Generate drafts"}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <p className="text-sm text-[#525252]">
              {result.items.length} draft{result.items.length === 1 ? "" : "s"} saved
              {result.discarded ? ` (${result.discarded} unusable response${result.discarded === 1 ? "" : "s"} discarded)` : ""}.
              Written by {result.provider} / {result.model}. Review them below, then approve from the Drafts tab.
            </p>
            {result.items.map((it) => <EmailCard key={it.id} item={it} />)}
            <div className="flex justify-end">
              <Button className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-white" onClick={onClose}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Edit dialog ────────────────────────────────────────────────────────────
function EditDialog({ item, options, onClose, onSaved }) {
  const [f, setF] = useState({
    subject: item.subject || "", opening: item.opening || "", feature: item.feature || "",
    cta_type: item.cta_type === "link" ? "link" : "reply", cta_label: item.cta_label || "",
    cta_link_key: item.cta_link_key || "", cross_sell: item.cross_sell || "", theme: item.theme || "",
  });
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const words = copyWordCount(f);
  const reapproval = item.status !== "draft";
  const payload = { ...f, cta_link_key: f.cta_type === "link" ? f.cta_link_key || null : null, theme: f.theme || null };
  const missing = ["subject", "opening", "feature", "cta_label", "cross_sell"].filter((k) => !String(f[k]).trim())
    .concat(f.cta_type === "link" && !f.cta_link_key ? ["link"] : []);

  const save = async () => {
    if (missing.length) { toast.error(`Please fill in: ${missing.join(", ").replace(/_/g, " ")}.`); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/marketing/nurture-content/${item.id}`, payload);
      toast.success(r.data.returned_to_draft
        ? "Saved. The item is back in Drafts and will not be sent until it is re-approved."
        : "Saved.");
      onSaved?.();
    } catch (e) {
      toast.error(errorText(e, "Could not save the changes. Nothing was changed."));
    }
    setSaving(false);
  };

  const area = (k, label, rows, help) => (
    <div>
      <Label className="text-xs">{label}{help && <span className="text-[#A3A3A3]"> — {help}</span>}</Label>
      <Textarea rows={rows} value={f[k]} onChange={set(k)} className="rounded-none font-sans text-sm" data-testid={`nurture-edit-${k}`} />
    </div>
  );

  return (
    <Dialog open onOpenChange={() => !saving && onClose()}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-2xl max-h-[94vh] overflow-y-auto" data-testid="nurture-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-black text-xl tracking-tight" style={H}>Edit nurture email</DialogTitle>
          <DialogDescription className="text-xs text-[#525252]">
            Only the message is edited here. The Eyvo header, “Hi {"{{FirstName}}"},” greeting, signature, legal footer and
            unsubscribe link come from the standard template. Links come only from the approved link list.
          </DialogDescription>
        </DialogHeader>
        {reapproval && (
          <div className="border border-[#FDE68A] bg-[#FFFBEB] p-2.5 text-xs text-[#92400E] flex gap-1.5">
            <Info size={14} className="shrink-0 mt-0.5" />
            This item is {item.status}. Saving changes returns it to Draft and removes it from sending until an admin re-approves it.
          </div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Subject</Label>
              <Input value={f.subject} maxLength={300} onChange={set("subject")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs">Module</Label>
              <select className={`${selectCls} w-full`} value={f.theme} onChange={set("theme")}>
                <option value="">(none)</option>
                {(options?.themes || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
          </div>
          {area("opening", "Opening", 2, "a short hook")}
          {area("feature", "Feature paragraph", 3, "one capability, real substance, no invented claims")}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Call to action</Label>
              <select className={`${selectCls} w-full`} value={f.cta_type} onChange={set("cta_type")}>
                <option value="link">Button link</option>
                <option value="reply">Invite a reply</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">{f.cta_type === "link" ? "Button text" : "Reply sentence"}</Label>
              <Input value={f.cta_label} maxLength={200} onChange={set("cta_label")} className="rounded-none" />
            </div>
          </div>
          {f.cta_type === "link" && (
            <div>
              <Label className="text-xs">Link (approved Eyvo pages only)</Label>
              <select className={`${selectCls} w-full`} value={f.cta_link_key} onChange={set("cta_link_key")} data-testid="nurture-edit-link">
                <option value="">Choose a page…</option>
                {(options?.links || []).map((l) => <option key={l.key} value={l.key}>{l.label} — {l.url}</option>)}
              </select>
            </div>
          )}
          {area("cross_sell", "Cross-sell paragraph", 2, "one short paragraph naming other Eyvo modules")}
          <p className={`text-[11px] ${words > WORD_RANGE[1] ? "text-[#B91C1C]" : "text-[#A3A3A3]"}`}>
            {words} words of copy (aim for {WORD_RANGE[0]}–{WORD_RANGE[1]}). Lint is re-checked when you save.
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="outline" className="rounded-none" onClick={() => setPreviewing(true)} disabled={saving || missing.length > 0}
            data-testid="nurture-edit-preview"><Eye size={14} className="mr-1.5" /> Preview changes</Button>
          <Button className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-white" onClick={save} disabled={saving}>
            {saving ? "Saving…" : reapproval ? "Save and return to Draft" : "Save"}
          </Button>
        </div>
        {previewing && <PreviewDialog item={item} edits={payload} onClose={() => setPreviewing(false)} />}
      </DialogContent>
    </Dialog>
  );
}

// ── Test-send dialog ───────────────────────────────────────────────────────
function TestSendDialog({ item, defaultEmail, onClose }) {
  const [to, setTo] = useState(defaultEmail || "");
  const [leadId, setLeadId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const send = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/marketing/nurture-content/${item.id}/test-send`,
        { to_email: to.trim(), ...(leadId.trim() ? { lead_id: leadId.trim() } : {}) });
      toast.success(r.data.message || `Test sent to ${to}`);
      onClose();
    } catch (e) {
      setError(errorText(e, "The test email was not sent."));
    }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={() => !busy && onClose()}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-md" data-testid="nurture-test-dialog">
        <DialogHeader>
          <DialogTitle className="font-black tracking-tight" style={H}>Send a test email</DialogTitle>
          <DialogDescription className="text-xs text-[#525252]">
            Sends one copy of “{item.subject}”, exactly as a lead would receive it, with “[TEST]” in the subject.
            All links work and are tracked. It is not counted in this email's analytics, and its unsubscribe
            link unsubscribes nobody.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Send to</Label>
            <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@eyvo.com"
              className="rounded-none" data-testid="nurture-test-to" autoFocus />
          </div>
          <div>
            <Label className="text-xs">Personalise as lead <span className="text-[#A3A3A3]">(optional — otherwise “Robert, Example Company”)</span></Label>
            <Input value={leadId} onChange={(e) => setLeadId(e.target.value)} placeholder="Lead ref or id" className="rounded-none" />
          </div>
          {error && <div className="border border-[#FECACA] bg-[#FEF2F2] p-2.5 text-sm text-[#B91C1C]" role="alert">{error}</div>}
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1 rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="flex-1 rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-white" onClick={send}
            disabled={busy || !/^\S+@\S+\.\S+$/.test(to.trim())} data-testid="nurture-test-send">
            <PaperPlaneTilt size={14} className="mr-1.5" /> {busy ? "Sending…" : "Send test"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Confirm dialog (approve / reject / retire / activate / bulk) ──────────
function ConfirmDialog({ confirm, onClose }) {
  const [notes, setNotes] = useState("");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!confirm) return null;
  const go = async () => {
    setBusy(true);
    try { await confirm.run({ notes: notes || null, override }); onClose(); } catch { /* run() reports */ }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={() => !busy && onClose()}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-md" data-testid="nurture-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-black tracking-tight" style={H}>{confirm.title}</DialogTitle>
          <DialogDescription className="text-xs text-[#525252]">{confirm.description}</DialogDescription>
        </DialogHeader>
        {confirm.body}
        {confirm.withNotes && (
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea rows={2} value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} className="rounded-none" />
          </div>
        )}
        {confirm.allowOverride && (
          <label className="flex items-start gap-2 text-xs text-[#92400E]">
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} className="mt-0.5" />
            Approve even though blocking lint flags are present (I have read the flagged text).
          </label>
        )}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1 rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className={`flex-1 rounded-none text-white ${confirm.danger ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-[#0A0A0A] hover:bg-[#262626]"}`}
            onClick={go} disabled={busy} data-testid="nurture-confirm-go">
            {busy ? "Working…" : confirm.cta}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────
export default function NurtureContentPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState("draft");
  const [theme, setTheme] = useState("");
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const params = {};
      if (status) params.status = status;
      if (theme) params.theme = theme;
      const r = await api.get("/marketing/nurture-content", { params });
      setData(r.data);
      setSelected((sel) => sel.filter((id) => r.data.items.some((i) => i.id === id)));
    } catch (e) {
      setLoadError(errorText(e, "Could not load the nurture content library. Please refresh the page."));
    }
    setLoading(false);
  }, [status, theme]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/marketing/nurture-content/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const items = useMemo(() => data?.items || [], [data]);
  const counts = data?.counts || {};
  const drafts = items.filter((i) => i.status === "draft");

  const act = async (item, action, body = {}) => {
    setBusyId(item.id);
    try {
      await api.post(`/marketing/nurture-content/${item.id}/${action}`, body,
        action === "regenerate" ? { timeout: LONG_TIMEOUT_MS } : undefined);
      const msg = {
        approve: "Approved. This email can now be sent by the nurture workflow.",
        reject: "Rejected. It will never be sent.",
        retire: "Retired. It will not be sent again.",
        activate: "Marked active.",
        regenerate: "Rewritten by Pulse AI. It is still a draft.",
      }[action];
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(errorText(e, `Could not ${action} this item. Nothing was changed.`));
      setBusyId(null);
      throw e;
    }
    setBusyId(null);
  };

  const askApprove = (item) => setConfirm({
    title: "Approve this email?",
    description: "Once approved it becomes eligible for the long-term nurture rotation and may be sent to real leads.",
    body: <EmailCard item={item} />,
    withNotes: true,
    allowOverride: blockingFlags(item).length > 0,
    cta: "Approve",
    run: ({ notes, override }) => act(item, "approve", { notes, override }),
  });
  const askReject = (item) => setConfirm({
    title: "Reject this draft?", description: "Rejected drafts are never sent. You can still edit one later to bring it back as a draft.",
    withNotes: true, danger: true, cta: "Reject",
    run: ({ notes }) => act(item, "reject", { notes }),
  });
  const askRetire = (item) => setConfirm({
    title: "Retire this email?", description: "It will be taken out of rotation immediately and never sent again. Past analytics are kept.",
    withNotes: true, danger: true, cta: "Retire",
    run: ({ notes }) => act(item, "retire", { notes }),
  });

  const askBulk = () => {
    const plan = bulkApprovalPlan(items, selected);
    const flagged = plan.skipped.filter((s) => s.reason === "has blocking lint flags").length;
    setConfirm({
      title: `Approve ${selected.length} selected draft${selected.length === 1 ? "" : "s"}?`,
      description: "Approved emails become eligible to be sent to real leads by the nurture workflow.",
      body: (
        <div className="text-sm text-[#525252] space-y-1">
          <p>{plan.approvable.length} will be approved.</p>
          {flagged > 0 && <p className="text-[#B91C1C]">{flagged} have blocking lint flags and will be skipped unless you tick the override below.</p>}
          {plan.skipped.length - flagged > 0 && <p>{plan.skipped.length - flagged} are not drafts and will be skipped.</p>}
        </div>
      ),
      allowOverride: flagged > 0,
      cta: "Approve selected",
      run: async ({ override }) => {
        try {
          const r = await api.post("/marketing/nurture-content/bulk-approve", { ids: selected, override });
          toast.success(`${r.data.approved.length} approved${r.data.skipped.length ? `, ${r.data.skipped.length} skipped` : ""}.`);
          setSelected([]);
          await load();
        } catch (e) {
          toast.error(errorText(e, "Bulk approval failed. Nothing was approved."));
          throw e;
        }
      },
    });
  };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allDraftIds = drafts.map((d) => d.id);
  const allSelected = allDraftIds.length > 0 && allDraftIds.every((id) => selected.includes(id));

  return (
    <MarketingShell>
      <div className="page-enter space-y-5" data-testid="nurture-content-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-3xl font-black tracking-tighter text-[#0A0A0A]" style={H}>Nurture Content</h2>
            <p className="text-sm text-[#525252] mt-1 max-w-3xl">
              A rotating library of short check-in emails for long-term leads in the
              “Eyvo Long-term Nurture (post-drip)” workflow. Pulse AI writes drafts; a person reviews them.
            </p>
          </div>
          <Button onClick={() => setGenerating(true)} className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-white" data-testid="nurture-generate-btn">
            <Sparkle size={14} className="mr-1.5" /> Generate nurture emails with AI
          </Button>
        </div>

        <div className="border border-[#BFDBFE] bg-[#EFF6FF] p-3 text-xs text-[#1E3A8A] flex gap-2" data-testid="nurture-help">
          <Info size={16} className="shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p><strong>Only Approved and Active emails are ever sent.</strong> Drafts, Rejected and Retired emails are never sent.
              Editing an approved email puts it back into Drafts until it is approved again.</p>
            <p>Each email covers one Eyvo module: opening, feature paragraph, call to action and a short cross-sell (about
              {" "}{WORD_RANGE[0]}–{WORD_RANGE[1]} words). Pulse adds the standard Eyvo header, signature, legal footer and unsubscribe.
              Use <strong>Preview</strong> to see the exact email before approving.</p>
            <p>Lint flags: <span className="text-[#B91C1C] font-semibold">red</span> = blocking (banned phrase, unsupported claim such as
              percentages, ROI, savings or certifications, an unapproved link, or over 130 words) — fix before approving;
              <span className="text-[#92400E] font-semibold"> amber</span> = advisory.
              Opens and clicks are human-only (bot opens excluded); a reply counts when the lead genuinely replies within
              {" "}{options?.reply_attribution_days || 30} days of the send. Opening or clicking never changes a lead's status.</p>
            <p><strong>No lead ever receives the same nurture email twice.</strong> Long-term Nurture sends one
              email every 45 days (moved to the recipient's working hours), each time choosing an approved email that lead has not had yet,
              preferring a different module from their last one. New emails are never generated automatically — use
              “Generate nurture emails with AI”, then review and approve.</p>
            <p>{data ? `${data.sendable_count} email${data.sendable_count === 1 ? "" : "s"} currently in rotation.` : ""}</p>
            {data?.runway && data.runway.approved > 0 && (data.runway.exhausted > 0 || data.runway.low > 0) && (
              <p className="text-[#92400E] font-semibold" data-testid="nurture-runway-warning">
                {data.runway.exhausted} lead{data.runway.exhausted === 1 ? " has" : "s have"} received every approved email
                (their next nurture step is skipped rather than repeated) and {data.runway.low} have{" "}
                {data.runway.low_threshold} or fewer left. Generate and approve more emails.
              </p>
            )}
            {data?.runway && data.runway.approved === 0 && (
              <p className="text-[#92400E]" data-testid="nurture-runway-none">
                Nothing approved yet, so Long-term Nurture is still sending its built-in step emails.
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex flex-wrap gap-1" role="tablist">
            {STATUS_TABS.map((t) => (
              <button key={t.key || "all"} role="tab" aria-selected={status === t.key} title={t.help}
                onClick={() => { setStatus(t.key); setSelected([]); }}
                data-testid={`nurture-tab-${t.key || "all"}`}
                className={`text-xs font-semibold px-3 py-1.5 border ${status === t.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0A0A0A]"}`}>
                {t.label}{t.key ? ` (${counts[t.key] ?? 0})` : data ? ` (${data.total})` : ""}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <select className={selectCls} value={theme} onChange={(e) => setTheme(e.target.value)} aria-label="Filter by theme">
              <option value="">All themes</option>
              {(options?.themes || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            {selected.length > 0 && (
              <Button onClick={askBulk} className="rounded-none bg-[#047857] hover:bg-[#065F46] text-white h-9" data-testid="nurture-bulk-approve">
                <CheckCircle size={14} className="mr-1.5" /> Approve selected ({selected.length})
              </Button>
            )}
          </div>
        </div>

        {data?.analytics_errors?.length > 0 && (
          <p className="text-xs text-[#92400E]">Some analytics could not be loaded ({data.analytics_errors.join(", ")}); those columns may show zero.</p>
        )}

        {loading && !data ? (
          <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>
        ) : loadError ? (
          <div className="border border-[#FECACA] bg-[#FEF2F2] p-4 text-sm text-[#B91C1C]" role="alert">
            <p className="font-semibold">Could not load nurture content.</p>
            <p>{loadError}</p>
            <Button variant="outline" className="rounded-none mt-2" onClick={load}>Retry</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#E5E5E5] bg-white py-14 text-center" data-testid="nurture-empty">
            <Sparkle size={28} className="mx-auto text-[#A3A3A3] mb-2" />
            <p className="text-sm text-[#525252]">
              {status === "draft" ? "No drafts waiting for review." : `No ${status ? STATUS_STYLE[status]?.label.toLowerCase() : ""} emails${theme ? " for this theme" : ""}.`}
            </p>
            <p className="text-xs text-[#A3A3A3] mt-1">Generate a batch with Pulse AI, then review and approve the ones you like.</p>
          </div>
        ) : (
          <div className="border border-[#E5E5E5] bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#737373]">
                <tr>
                  <th className="p-2 w-8">
                    <input type="checkbox" aria-label="Select all drafts" disabled={!allDraftIds.length}
                      checked={allSelected} onChange={() => setSelected(allSelected ? [] : allDraftIds)} />
                  </th>
                  <th className="p-2 text-left">Status / theme</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-right" title="Words of copy before the footer">Words</th>
                  <th className="p-2 text-left">Lint</th>
                  <th className="p-2 text-right">Sends</th>
                  <th className="p-2 text-right" title="Delivered / sends">Deliv.</th>
                  <th className="p-2 text-right" title="Sends with at least one human (non-bot) open">Opens</th>
                  <th className="p-2 text-right" title="Sends with at least one human (non-bot) click">Clicks</th>
                  <th className="p-2 text-right" title="Sends whose human click went to this email's own call-to-action link">CTA</th>
                  <th className="p-2 text-right" title="Genuine replies within the attribution window">Replies</th>
                  <th className="p-2 text-right">Unsubs</th>
                  <th className="p-2 text-right" title="Permanent (hard) bounces">Bounces</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const a = it.analytics || {};
                  const busy = busyId === it.id;
                  return (
                    <tr key={it.id} className="border-t border-[#F0F0F0] align-top" data-testid={`nurture-row-${it.id}`}>
                      <td className="p-2">
                        {it.status === "draft" && (
                          <input type="checkbox" aria-label={`Select ${it.subject}`} checked={selected.includes(it.id)} onChange={() => toggleSel(it.id)} />
                        )}
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        <StatusBadge status={it.status} />
                        <div className="text-[11px] text-[#525252] mt-1">{it.theme_label || "—"}</div>
                        <div className="text-[10px] text-[#A3A3A3]">{it.created_by === "ai" ? "AI" : "Operator"}{it.cta_type ? ` · ${it.cta_type.replace(/_/g, " ")}` : ""}</div>
                        {isSendable(it.status) && <div className="text-[10px] text-[#047857] font-semibold mt-0.5">In rotation</div>}
                      </td>
                      <td className="p-2 max-w-[420px]">
                        <p className="font-semibold text-[#0A0A0A]">{it.subject}</p>
                        <p className="text-xs text-[#525252] mt-0.5" title={it.body_text}>{it.is_v2 ? preview(`${it.opening || ""} ${it.feature || ""}`) : preview(it.body_text)}</p>
                        {!it.is_v2 && <p className="text-[11px] text-[#92400E] mt-0.5">Old short format — regenerate or edit before it can be approved.</p>}
                        {it.review_notes && <p className="text-[11px] text-[#A3A3A3] mt-1">Note: {it.review_notes}</p>}
                      </td>
                      <td className={`p-2 text-right tabular-nums ${it.word_count > WORD_RANGE[1] ? "text-[#B91C1C] font-semibold" : ""}`}>{it.word_count}</td>
                      <td className="p-2 min-w-[140px]"><Flags item={it} /></td>
                      <td className="p-2 text-right tabular-nums">{a.sends || 0}</td>
                      <td className="p-2 text-right tabular-nums" title={rate(a.delivered, a.sends)}>{a.delivered || 0}</td>
                      <td className="p-2 text-right tabular-nums" title={rate(a.human_opens, a.delivered)}>{a.human_opens || 0}</td>
                      <td className="p-2 text-right tabular-nums" title={rate(a.human_clicks, a.delivered)}>{a.human_clicks || 0}</td>
                      <td className="p-2 text-right tabular-nums" title={rate(a.cta_clicks, a.delivered)}>{it.cta_type === "link" ? (a.cta_clicks || 0) : "reply"}</td>
                      <td className="p-2 text-right tabular-nums" title={rate(a.replies, a.delivered)}>{a.replies || 0}</td>
                      <td className="p-2 text-right tabular-nums">{a.unsubscribes || 0}</td>
                      <td className="p-2 text-right tabular-nums">{a.hard_bounces || 0}</td>
                      <td className="p-2">
                        <div className="flex justify-end gap-1 flex-wrap">
                          <button disabled={busy || !it.is_v2} onClick={() => setPreviewing(it)} title="Preview the exact email (never sends)"
                            aria-label="Preview" data-testid={`nurture-preview-${it.id}`}
                            className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider border border-[#E5E5E5] text-[#273b86] hover:border-[#273b86] disabled:opacity-40 inline-flex items-center gap-1">
                            <Eye size={13} /> Preview
                          </button>
                          <button disabled={busy || !it.is_v2} onClick={() => setTesting(it)} title="Send a test email to an address you choose"
                            aria-label="Send test" data-testid={`nurture-test-${it.id}`}
                            className="p-1.5 text-[#525252] hover:text-[#0055FF] hover:bg-[#F0F7FF] disabled:opacity-40">
                            <PaperPlaneTilt size={14} />
                          </button>
                          <button disabled={busy} onClick={() => setEditing(it)} title="Edit" aria-label="Edit"
                            className="p-1.5 text-[#525252] hover:text-[#0055FF] hover:bg-[#F0F7FF] disabled:opacity-40">
                            <PencilSimple size={14} />
                          </button>
                          {["draft", "rejected"].includes(it.status) && (
                            <button disabled={busy} onClick={() => act(it, "regenerate", {}).catch(() => {})} title="Regenerate with Pulse AI (stays a draft)" aria-label="Regenerate"
                              className="p-1.5 text-[#525252] hover:text-[#0055FF] hover:bg-[#F0F7FF] disabled:opacity-40">
                              <ArrowsClockwise size={14} className={busy ? "animate-spin" : ""} />
                            </button>
                          )}
                          {it.status === "draft" && (
                            <>
                              <button disabled={busy || !it.is_v2} onClick={() => setPreviewing({ ...it, _approve: true })} title="Preview, then approve" aria-label="Approve" data-testid={`nurture-approve-${it.id}`}
                                className="p-1.5 text-[#047857] hover:bg-[#ECFDF5] disabled:opacity-40"><CheckCircle size={14} /></button>
                              <button disabled={busy} onClick={() => askReject(it)} title="Reject" aria-label="Reject"
                                className="p-1.5 text-[#B91C1C] hover:bg-[#FEF2F2] disabled:opacity-40"><XCircle size={14} /></button>
                            </>
                          )}
                          {it.status === "approved" && (
                            <button disabled={busy} onClick={() => act(it, "activate").catch(() => {})} title="Mark active (it also becomes active automatically on first send)" aria-label="Activate"
                              className="p-1.5 text-[#1D4ED8] hover:bg-[#EFF6FF] disabled:opacity-40"><Play size={14} /></button>
                          )}
                          {isSendable(it.status) && (
                            <button disabled={busy} onClick={() => askRetire(it)} title="Retire (stop sending)" aria-label="Retire"
                              className="p-1.5 text-[#525252] hover:bg-[#FAFAFA] disabled:opacity-40"><Archive size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {generating && (
          <GenerateDialog options={options} onClose={() => { setGenerating(false); }}
            onGenerated={() => { setStatus("draft"); load(); }} />
        )}
        {editing && (
          <EditDialog item={editing} options={options} onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }} />
        )}
        {testing && (
          <TestSendDialog item={testing} defaultEmail={user?.email} onClose={() => setTesting(null)} />
        )}
        {previewing && (
          <PreviewDialog item={previewing} onClose={() => setPreviewing(null)}
            onApprove={previewing.status === "draft" ? (it) => { setPreviewing(null); askApprove(it); } : null} />
        )}
        <ConfirmDialog key={confirm?.title || "none"} confirm={confirm} onClose={() => setConfirm(null)} />
      </div>
    </MarketingShell>
  );
}
